<script setup>
// Mapa / diagrama PROFISSIONAL (v12 — jornada do cliente, 28/09/2026):
// - ARRASTAR livre (VueFlow), criar/conectar pelos 4 LADOS;
// - FORMATOS: balão, cartão, nota, texto, LOSANGO (decisão), PÍLULA e MOLDURA
//   (agrupa blocos; arrastar a moldura leva o que está dentro);
// - ÍCONE por bloco e TÍTULO + LISTA (1ª linha título, demais itens);
// - SETAS com ponta, RÓTULO ("SIM", "2 dias") e tracejado por linha;
// - TEMA do mapa: claro ou NEON (lousa de vidro, letra de marcador);
// - EXPORTAR em PNG (imagem) e PDF (impressão do navegador).
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow, Handle } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { useAlert } from 'dashboard/composables';
import { planningMapsAPI } from 'dashboard/api/workspace';
import kalam400 from 'dashboard/assets/fonts/kalam/kalam-400-latin.woff2';
import kalam700 from 'dashboard/assets/fonts/kalam/kalam-700-latin.woff2';
import {
  COLORS,
  DEFAULT_SIZE,
  ICONS,
  ICON_KEYS,
  edgeStyle,
  normalizeNode,
  normalizeTheme,
  nodesInsideFrame,
  splitList,
  facingSide,
  isClick,
  edgeOptions,
  LINE_PATTERNS,
  LINE_WIDTHS,
} from '../helpers/mapaHelpers';
import {
  downloadBlob,
  elementToPngBlob,
  exportFileName,
  fontFaceCss,
} from '../helpers/exportMapa';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Formatos de nó (pedido: "balão ou outro, tudo isso tem que poder escolher")
const SHAPES = [
  { key: 'balloon', icon: 'i-lucide-message-circle' },
  { key: 'card', icon: 'i-lucide-square' },
  { key: 'note', icon: 'i-lucide-sticky-note' },
  { key: 'text', icon: 'i-lucide-type' },
  { key: 'diamond', icon: 'i-lucide-diamond' },
  { key: 'pill', icon: 'i-lucide-rectangle-horizontal' },
  { key: 'frame', icon: 'i-lucide-frame' },
];

const SIDES = ['top', 'right', 'bottom', 'left'];
const OPPOSITE = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' };

// Letra de marcador do tema neon, empacotada no app (Kalam, licença OFL).
const HAND_FONTS = [
  { family: 'Kalam', weight: 400, url: kalam400 },
  { family: 'Kalam', weight: 700, url: kalam700 },
];
const NEON = { fundo: '#0a0d14', pontos: '#1a2031', texto: '#eceff6' };

const mapId = route.params.mapId;
const mapName = ref('');
const nodes = ref([]);
const edges = ref([]);
const isLoading = ref(true);
const saveState = ref('saved');
const selectedId = ref(null);
const editingId = ref(null);
const selectedEdgeId = ref(null);
const theme = ref('claro');
const isExporting = ref(false);
const flowWrapper = ref(null);
const edgeLabelInput = ref(null);

const {
  onConnect,
  addEdges,
  toObject,
  setViewport,
  screenToFlowCoordinate,
  findNode: vfNode,
  onNodeDragStart,
  onNodeDrag,
  onNodeDragStop,
  onEdgeClick,
  onEdgeDoubleClick,
  onConnectStart,
  onConnectEnd,
  fitView,
  viewport,
} = useVueFlow();

const selectedNode = computed(() =>
  nodes.value.find(n => n.id === selectedId.value)
);
const selectedEdge = computed(() =>
  edges.value.find(e => e.id === selectedEdgeId.value)
);
const isNeon = computed(() => theme.value === 'neon');

// ===== Linhas =====
const lineShape = ref('curve'); // curve | straight | elbow
const lineDashed = ref(false);
const LINE_SHAPES = [
  { key: 'curve', icon: 'i-lucide-spline' },
  { key: 'straight', icon: 'i-lucide-slash' },
  { key: 'elbow', icon: 'i-lucide-corner-down-right' },
];
const EDGE_TYPE = {
  curve: 'default',
  straight: 'straight',
  elbow: 'smoothstep',
};

// Valores efetivos da seta selecionada (inclui o formato antigo e o geral).
const edgeOpts = computed(() =>
  selectedEdge.value
    ? edgeOptions(selectedEdge.value.data || {}, lineDashed.value)
    : null
);

// Miniaturas da barra da seta: cada opção mostra o desenho da linha.
const EDGE_TYPE_OPTIONS = [
  { key: 'curve', d: 'M3 13 C 12 13, 17 3, 27 3' },
  { key: 'straight', d: 'M3 13 L27 3' },
  { key: 'elbow', d: 'M3 13 H13 Q15 13 15 11 V5 Q15 3 17 3 H27' },
  { key: 'step', d: 'M3 13 H15 V3 H27' },
];
const PATTERN_OPTIONS = Object.keys(LINE_PATTERNS);
const WIDTH_OPTIONS = Object.keys(LINE_WIDTHS);
const ARROW_OPTIONS = ['none', 'end', 'both'];

const colorOf = id =>
  nodes.value.find(n => n.id === id)?.data?.color || COLORS[0];

function edgeProps(color, edgeData = {}) {
  return edgeStyle({
    color,
    lineType: EDGE_TYPE[lineShape.value],
    globalDashed: lineDashed.value,
    edgeData,
  });
}

// Trocar o estilo geral re-pinta TODAS as linhas, preservando rótulo,
// tracejado próprio e ponta de cada uma.
watch([lineShape, lineDashed], () => {
  edges.value = edges.value.map(edge => ({
    ...edge,
    ...edgeProps(colorOf(edge.source), edge.data || {}),
  }));
});

// ===== Persistência =====
let saveTimer = null;
let suppressWatch = false;

async function persist() {
  saveState.value = 'saving';
  try {
    await planningMapsAPI.update(mapId, {
      name: mapName.value,
      data: {
        ...toObject(),
        line: { shape: lineShape.value, dashed: lineDashed.value },
        theme: theme.value,
      },
    });
    saveState.value = 'saved';
  } catch {
    saveState.value = 'dirty';
    useAlert(t('WORKSPACE_MAPS.SAVE_ERROR'));
  }
}

function scheduleSave() {
  if (suppressWatch) return;
  saveState.value = 'dirty';
  clearTimeout(saveTimer);
  saveTimer = setTimeout(persist, 1200);
}

watch([nodes, edges], scheduleSave, { deep: true });
watch(mapName, scheduleSave);
watch([lineShape, lineDashed, theme], scheduleSave);

// ===== Criação =====
let seq = Date.now();
const newId = () => {
  seq += 1;
  return `n-${seq}`;
};

function startEdit(id) {
  selectedId.value = id;
  selectedEdgeId.value = null;
  editingId.value = id;
  nextTick(() =>
    setTimeout(() => document.getElementById(`mapa-input-${id}`)?.focus(), 50)
  );
}

function makeNode({ position, color, shape, text = '', fontSize = 14 }) {
  return normalizeNode({
    id: newId(),
    position,
    data: { label: text, color, shape, fontSize },
  });
}

function patchNode(id, patch) {
  const node = nodes.value.find(n => n.id === id);
  if (node) node.data = { ...node.data, ...patch };
  return node;
}

// Tamanho da fonte por nó (pedido Ivan: aumentar/diminuir).
function bumpFontSize(id, delta) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  const atual = node.data.fontSize || 14;
  patchNode(id, { fontSize: Math.min(32, Math.max(10, atual + delta)) });
}

// Fixar nó (pedido Ivan: "opção de deixar fixo"): trava o arrasto.
function toggleLock(id) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  const travado = node.data.locked !== true;
  node.draggable = !travado;
  patchNode(id, { locked: travado });
}

function centerPosition() {
  return screenToFlowCoordinate({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
}

function addFreeNode(shape = 'balloon') {
  const node = makeNode({
    position: centerPosition(),
    color: COLORS[nodes.value.length % COLORS.length],
    shape,
  });
  nodes.value = [...nodes.value, node];
  startEdit(node.id);
}

// Cria um filho NA DIREÇÃO pedida (4 lados), conectado da alça certa.
function addChildAt(parentId, side) {
  const pai = nodes.value.find(n => n.id === parentId);
  if (!pai) return;
  const medida = vfNode(parentId)?.dimensions || { width: 160, height: 44 };
  const irmaos = edges.value.filter(
    e => e.source === parentId && e.sourceHandle === side
  ).length;
  const desloca = irmaos * 90;
  const OFFSET = 190;
  const posicoes = {
    right: {
      x: pai.position.x + medida.width + OFFSET,
      y: pai.position.y + desloca,
    },
    left: {
      x: pai.position.x - medida.width - OFFSET,
      y: pai.position.y + desloca,
    },
    top: { x: pai.position.x + desloca, y: pai.position.y - 130 },
    bottom: {
      x: pai.position.x + desloca,
      y: pai.position.y + medida.height + 90,
    },
  };
  // Filho de losango nasce cartão: a decisão leva a um passo, não a outra decisão.
  const formaFilho = ['diamond', 'frame'].includes(pai.data.shape)
    ? 'card'
    : pai.data.shape || 'balloon';
  const filho = makeNode({
    position: posicoes[side],
    color: pai.data.color,
    shape: formaFilho,
    fontSize: pai.data.fontSize || 14,
  });
  nodes.value = [...nodes.value, filho];
  edges.value = [
    ...edges.value,
    {
      id: `e-${parentId}-${filho.id}`,
      source: parentId,
      target: filho.id,
      sourceHandle: side,
      targetHandle: OPPOSITE[side],
      ...edgeProps(pai.data.color),
    },
  ];
  startEdit(filho.id);
}

function addSibling(id) {
  const paiEdge = edges.value.find(e => e.target === id);
  if (paiEdge) addChildAt(paiEdge.source, paiEdge.sourceHandle || 'right');
  else addFreeNode();
}

function removeNode(id) {
  nodes.value = nodes.value.filter(n => n.id !== id);
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id);
  selectedId.value = null;
  editingId.value = null;
}

function setNodeColor(id, color) {
  patchNode(id, { color });
  edges.value = edges.value.map(e =>
    e.source === id ? { ...e, ...edgeProps(color, e.data || {}) } : e
  );
}

function setNodeShape(id, shape) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  const size = DEFAULT_SIZE[shape] || {};
  const atualizado = normalizeNode({
    ...node,
    data: {
      ...node.data,
      shape,
      width: size.width || node.data.width,
      height: size.height || node.data.height,
    },
  });
  nodes.value = nodes.value.map(n => (n.id === id ? atualizado : n));
}

function setNodeIcon(id, icon) {
  patchNode(id, { icon: icon || undefined });
}

function toggleList(id) {
  const node = nodes.value.find(n => n.id === id);
  if (node) patchNode(id, { list: !node.data.list });
}

// ===== Ligar blocos =====
// O "+" de cada lado É a alça: clicar cria o próximo bloco, arrastar puxa uma
// seta. Soltar em QUALQUER parte de outro bloco liga os dois (antes só ligava
// soltando em cima da bolinha de 8px, que ainda ficava escondida sob o "+").
let puxando = null;
let conectou = false;

const pointOf = event => {
  const ponto = event?.changedTouches?.[0] || event?.touches?.[0] || event;
  return ponto ? { x: ponto.clientX, y: ponto.clientY } : null;
};
const boxOf = node => ({
  x: node.computedPosition?.x ?? node.position.x,
  y: node.computedPosition?.y ?? node.position.y,
  width: node.dimensions?.width || 0,
  height: node.dimensions?.height || 0,
});

onConnect(params => {
  conectou = true;
  addEdges([{ ...params, ...edgeProps(colorOf(params.source)) }]);
});

onConnectStart(({ event, nodeId, handleId }) => {
  conectou = false;
  puxando = { nodeId, handleId: handleId || 'right', inicio: pointOf(event) };
});

onConnectEnd(event => {
  const origem = puxando;
  puxando = null;
  if (!origem || conectou) return;
  const fim = pointOf(event);
  if (isClick(origem.inicio, fim)) {
    addChildAt(origem.nodeId, origem.handleId);
    return;
  }
  const alvoId = fim
    ? document
        .elementFromPoint(fim.x, fim.y)
        ?.closest('.vue-flow__node')
        ?.getAttribute('data-id')
    : null;
  const alvo = alvoId && vfNode(alvoId);
  const saida = vfNode(origem.nodeId);
  // Moldura agrupa, não recebe seta; soltar no próprio bloco não liga nada.
  if (!alvo || !saida || alvoId === origem.nodeId || alvo.type === 'frame') {
    return;
  }
  addEdges([
    {
      id: `e-${origem.nodeId}-${alvoId}-${Date.now()}`,
      source: origem.nodeId,
      target: alvoId,
      sourceHandle: origem.handleId,
      targetHandle: facingSide(boxOf(saida), boxOf(alvo)),
      ...edgeProps(colorOf(origem.nodeId)),
    },
  ]);
});

// ===== Setas: rótulo, tracejado, ponta =====
function selectEdge(id) {
  selectedEdgeId.value = id;
  selectedId.value = null;
  editingId.value = null;
}

function patchEdge(id, patch) {
  edges.value = edges.value.map(edge => {
    if (edge.id !== id) return edge;
    const data = { ...(edge.data || {}), ...patch };
    return { ...edge, ...edgeProps(colorOf(edge.source), data) };
  });
}

function removeEdge(id) {
  edges.value = edges.value.filter(e => e.id !== id);
  selectedEdgeId.value = null;
}

onEdgeClick(({ edge }) => selectEdge(edge.id));
onEdgeDoubleClick(({ edge }) => {
  selectEdge(edge.id);
  nextTick(() => edgeLabelInput.value?.focus());
});

// ===== Moldura: arrastar leva quem está dentro =====
let grupo = null;
const sizeOf = node => vfNode(node.id)?.dimensions;

onNodeDragStart(({ node }) => {
  const frame = nodes.value.find(n => n.id === node.id);
  if (frame?.data?.shape !== 'frame') {
    grupo = null;
    return;
  }
  const membros = nodesInsideFrame(frame, nodes.value, sizeOf);
  grupo = {
    frameId: frame.id,
    origem: { ...node.position },
    membros: membros.map(id => ({
      id,
      inicio: { ...nodes.value.find(n => n.id === id).position },
    })),
  };
});

onNodeDrag(({ node }) => {
  if (!grupo || node.id !== grupo.frameId) return;
  const dx = node.position.x - grupo.origem.x;
  const dy = node.position.y - grupo.origem.y;
  grupo.membros.forEach(({ id, inicio }) => {
    const membro = nodes.value.find(n => n.id === id);
    if (membro) membro.position = { x: inicio.x + dx, y: inicio.y + dy };
  });
});

onNodeDragStop(() => {
  grupo = null;
});

// Redimensionar moldura/losango pelo canto (pontos de tela → zoom do canvas).
let resize = null;
function onResizeMove(event) {
  if (!resize) return;
  const zoom = viewport.value?.zoom || 1;
  const width = Math.max(
    resize.min.width,
    resize.width + (event.clientX - resize.x) / zoom
  );
  const height = Math.max(
    resize.min.height,
    resize.height + (event.clientY - resize.y) / zoom
  );
  patchNode(resize.id, {
    width: Math.round(width),
    height: Math.round(height),
  });
}
function stopResize() {
  resize = null;
  window.removeEventListener('pointermove', onResizeMove);
  window.removeEventListener('pointerup', stopResize);
}
function startResize(event, id) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  resize = {
    id,
    x: event.clientX,
    y: event.clientY,
    width: node.data.width,
    height: node.data.height,
    min:
      node.data.shape === 'frame'
        ? { width: 160, height: 100 }
        : { width: 120, height: 70 },
  };
  window.addEventListener('pointermove', onResizeMove);
  window.addEventListener('pointerup', stopResize);
}

// ===== Aparência por tema =====
function nodeBoxClass(data) {
  const shape = data.shape || 'balloon';
  if (isNeon.value) {
    return [
      'mapa-neon-no',
      shape === 'pill' || shape === 'balloon' ? 'rounded-full' : 'rounded-2xl',
      shape === 'text' ? 'mapa-neon-texto' : '',
    ];
  }
  return [
    shape === 'balloon'
      ? 'rounded-full border-2 text-n-slate-12 bg-n-solid-1'
      : '',
    shape === 'pill'
      ? 'rounded-full border-2 text-n-slate-12 bg-n-solid-1 !py-1.5'
      : '',
    shape === 'card' ? 'rounded-xl text-white shadow-md' : '',
    shape === 'note' ? 'rounded-md border text-slate-800 shadow-md' : '',
    shape === 'text' ? 'mapa-no-texto text-n-slate-12' : '',
  ];
}

function nodeBoxStyle(data) {
  const cor = data.color;
  if (isNeon.value) {
    if (data.shape === 'text')
      return { '--c': cor, borderBottom: `2.5px solid ${cor}` };
    return { '--c': cor };
  }
  if (data.shape === 'card') return { backgroundColor: cor };
  if (data.shape === 'note') {
    return {
      backgroundColor: `${cor}33`,
      borderColor: `${cor}66`,
      color: 'inherit',
    };
  }
  if (data.shape === 'text') return { borderBottom: `2.5px solid ${cor}` };
  return { borderColor: cor, backgroundColor: `${cor}1f` };
}

// ===== Exportar =====
async function exportPng() {
  if (isExporting.value) return;
  isExporting.value = true;
  selectedId.value = null;
  selectedEdgeId.value = null;
  editingId.value = null;
  try {
    await fitView({ padding: 0.12 });
    await nextTick();
    await new Promise(resolve => {
      setTimeout(resolve, 350);
    });
    const alvo = flowWrapper.value?.querySelector('.vue-flow');
    const fontCss = isNeon.value ? await fontFaceCss(HAND_FONTS) : '';
    const blob = await elementToPngBlob(alvo, {
      background: isNeon.value ? NEON.fundo : '#ffffff',
      fontCss,
    });
    downloadBlob(blob, exportFileName(mapName.value, 'png'));
  } catch {
    useAlert(t('WORKSPACE_MAPS.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
}

async function exportPdf() {
  selectedId.value = null;
  selectedEdgeId.value = null;
  editingId.value = null;
  await fitView({ padding: 0.08 });
  await new Promise(resolve => {
    setTimeout(resolve, 300);
  });
  window.print();
}

// ===== Teclado =====
function isTyping(event) {
  const tag = event.target?.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable
  );
}

function onKeydown(event) {
  if (editingId.value) {
    if (event.key === 'Escape') editingId.value = null;
    // Nome da moldura: Enter/Tab só encerram a edição (moldura não tem filho).
    const emEdicao = nodes.value.find(n => n.id === editingId.value);
    if (emEdicao?.data?.shape === 'frame') {
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        editingId.value = null;
      }
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const atual = editingId.value;
      editingId.value = null;
      addChildAt(atual, 'right');
    }
    // Bloco com lista: Enter é item novo (quebra de linha), não irmão.
    if (event.key === 'Enter' && !event.shiftKey && !emEdicao?.data?.list) {
      event.preventDefault();
      const atual = editingId.value;
      editingId.value = null;
      addSibling(atual);
    }
    return;
  }
  if (isTyping(event)) return;
  if (
    selectedEdgeId.value &&
    (event.key === 'Delete' || event.key === 'Backspace')
  ) {
    event.preventDefault();
    removeEdge(selectedEdgeId.value);
    return;
  }
  if (!selectedId.value) return;
  if (event.key === 'Tab') {
    event.preventDefault();
    addChildAt(selectedId.value, 'right');
  } else if (event.key === 'Enter') {
    event.preventDefault();
    addSibling(selectedId.value);
  } else if (event.key === 'Delete' || event.key === 'Backspace') {
    event.preventDefault();
    removeNode(selectedId.value);
  } else if (event.key === 'F2') {
    startEdit(selectedId.value);
  }
}

// ===== Load (aceita v2 nodes/edges, v3 tree e o formato atual) =====
function treeToGraph(arvore) {
  const saida = { nodes: [], edges: [] };
  const anda = (no, x, y, cor) => {
    const corNo = no.color || cor || COLORS[0];
    saida.nodes.push(
      normalizeNode({
        id: no.id,
        position: { x, y },
        data: { label: no.text || '', color: corNo, shape: 'balloon' },
      })
    );
    let cursor = y - ((no.children?.length || 1) - 1) * 60;
    (no.children || []).forEach(filho => {
      saida.edges.push({
        id: `e-${no.id}-${filho.id}`,
        source: no.id,
        target: filho.id,
        sourceHandle: 'right',
        targetHandle: 'left',
        data: { arrow: false },
      });
      anda(filho, x + 320, cursor, filho.color || corNo);
      cursor += 120;
    });
  };
  anda(arvore, 0, 0, arvore.color);
  return saida;
}

async function loadHandFont() {
  if (!document.fonts || typeof FontFace === 'undefined') return;
  try {
    await Promise.all(
      HAND_FONTS.map(async ({ family, weight, url }) => {
        const face = new FontFace(family, `url(${url}) format('woff2')`, {
          weight: String(weight),
        });
        document.fonts.add(await face.load());
      })
    );
  } catch {
    // Sem a letra de marcador o tema neon usa a letra padrão — não trava o mapa.
  }
}

async function load() {
  try {
    const { data } = await planningMapsAPI.show(mapId);
    mapName.value = data.name;
    suppressWatch = true;
    lineShape.value = data.data?.line?.shape || 'curve';
    lineDashed.value = !!data.data?.line?.dashed;
    theme.value = normalizeTheme(data.data?.theme);
    let grafo = { nodes: [], edges: [] };
    if (data.data?.nodes?.length) {
      grafo = { nodes: data.data.nodes, edges: data.data.edges || [] };
    } else if (data.data?.tree) {
      grafo = treeToGraph(data.data.tree);
    }
    nodes.value = grafo.nodes.map(normalizeNode);
    // Setas de mapas antigos (sem `data`) seguem SEM ponta: o visual de quem
    // já tinha mapa não muda sozinho.
    edges.value = grafo.edges.map(e => ({
      ...e,
      sourceHandle: e.sourceHandle || 'right',
      targetHandle: e.targetHandle || 'left',
      ...edgeProps(colorOf(e.source), e.data || { arrow: false }),
    }));
    if (data.data?.viewport) setViewport(data.data.viewport);
    setTimeout(() => {
      suppressWatch = false;
    }, 300);
  } catch {
    useAlert(t('WORKSPACE_MAPS.LOAD_ERROR'));
    router.push({ name: 'planning_maps_index' });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadHandFont();
  load();
  window.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  stopResize();
  clearTimeout(saveTimer);
  if (saveState.value === 'dirty') persist();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <div
      class="mapa-sem-impressao flex flex-wrap items-center gap-3 px-4 py-3 border-b border-n-weak bg-n-background z-10"
    >
      <button
        class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
        @click="router.push({ name: 'planning_maps_index' })"
      >
        <span class="i-lucide-arrow-left w-4 h-4" />
      </button>
      <input
        v-model="mapName"
        class="flex-1 min-w-[160px] bg-transparent text-sm font-semibold text-n-slate-12 outline-none border-b border-transparent focus:border-n-brand"
        :placeholder="t('WORKSPACE_MAPS.NAME_PLACEHOLDER')"
      />
      <!-- Estilo das linhas -->
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-2 p-0.5"
        :title="t('WORKSPACE_MAPS.LINE_STYLE')"
      >
        <button
          v-for="shape in LINE_SHAPES"
          :key="shape.key"
          class="p-1.5 rounded-lg transition-colors"
          :class="
            lineShape === shape.key
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t(`WORKSPACE_MAPS.LINE_${shape.key.toUpperCase()}`)"
          @click="lineShape = shape.key"
        >
          <span :class="shape.icon" class="w-4 h-4" />
        </button>
        <button
          class="p-1.5 rounded-lg transition-colors"
          :class="
            lineDashed
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t('WORKSPACE_MAPS.LINE_DASHED')"
          @click="lineDashed = !lineDashed"
        >
          <span class="i-lucide-tally-3 w-4 h-4" />
        </button>
      </div>
      <!-- Tema do mapa -->
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-2 p-0.5"
        :title="t('WORKSPACE_MAPS.THEME')"
      >
        <button
          class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition-colors"
          :class="
            !isNeon
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="theme = 'claro'"
        >
          <span class="i-lucide-sun w-3.5 h-3.5" />
          {{ t('WORKSPACE_MAPS.THEME_LIGHT') }}
        </button>
        <button
          class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition-colors"
          :class="
            isNeon
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="theme = 'neon'"
        >
          <span class="i-lucide-sparkles w-3.5 h-3.5" />
          {{ t('WORKSPACE_MAPS.THEME_NEON') }}
        </button>
      </div>
      <!-- Exportar -->
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-2 p-0.5"
        :title="t('WORKSPACE_MAPS.EXPORT')"
      >
        <button
          class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors disabled:opacity-50"
          :disabled="isExporting"
          @click="exportPng"
        >
          <span
            :class="
              isExporting
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-image-down'
            "
            class="w-3.5 h-3.5"
          />
          {{ t('WORKSPACE_MAPS.EXPORT_PNG') }}
        </button>
        <button
          class="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="exportPdf"
        >
          <span class="i-lucide-file-down w-3.5 h-3.5" />
          {{ t('WORKSPACE_MAPS.EXPORT_PDF') }}
        </button>
      </div>
      <!-- Atalhos viram tooltip no "?" (o texto solto poluía o header) -->
      <button
        class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
        :title="t('WORKSPACE_MAPS.SHORTCUTS')"
      >
        <span class="i-lucide-circle-help w-4 h-4" />
      </button>
      <span class="text-xs text-n-slate-9">
        {{
          saveState === 'saved'
            ? t('WORKSPACE_MAPS.SAVED')
            : t('WORKSPACE_MAPS.SAVING')
        }}
      </span>
      <button
        class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-xl bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
        @click="addFreeNode()"
      >
        <span class="i-lucide-plus w-4 h-4" />
        {{ t('WORKSPACE_MAPS.ADD_IDEA') }}
      </button>
    </div>

    <!-- Barra de peças: cria o formato no centro da tela -->
    <div
      class="mapa-sem-impressao flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-n-weak bg-n-solid-1 z-10"
    >
      <span class="text-xs text-n-slate-10 mr-1">
        {{ t('WORKSPACE_MAPS.ADD_SHAPE') }}
      </span>
      <button
        v-for="shape in SHAPES"
        :key="`nova-${shape.key}`"
        class="flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
        @click="addFreeNode(shape.key)"
      >
        <span :class="shape.icon" class="w-3.5 h-3.5" />
        {{ t(`WORKSPACE_MAPS.SHAPE_${shape.key.toUpperCase()}`) }}
      </button>
    </div>

    <!-- Barra do nó selecionado: FORMATO + cor + ícone + lista + ações -->
    <div
      v-if="selectedNode"
      class="mapa-sem-impressao flex flex-wrap items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-solid-2 z-10"
    >
      <span class="text-xs text-n-slate-10">
        {{ t('WORKSPACE_MAPS.SHAPE') }}
      </span>
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
      >
        <button
          v-for="shape in SHAPES"
          :key="shape.key"
          class="p-1.5 rounded-lg transition-colors"
          :class="
            (selectedNode.data.shape || 'balloon') === shape.key
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t(`WORKSPACE_MAPS.SHAPE_${shape.key.toUpperCase()}`)"
          @click="setNodeShape(selectedNode.id, shape.key)"
        >
          <span :class="shape.icon" class="w-4 h-4" />
        </button>
      </div>
      <span class="ml-2 text-xs text-n-slate-10">
        {{ t('WORKSPACE_MAPS.BRANCH_COLOR') }}
      </span>
      <button
        v-for="color in COLORS"
        :key="color"
        class="w-4 h-4 rounded-full border-2 transition-transform hover:scale-110"
        :class="
          selectedNode.data.color === color
            ? 'border-n-slate-12'
            : 'border-transparent'
        "
        :style="{ backgroundColor: color }"
        @click="setNodeColor(selectedNode.id, color)"
      />
      <!-- Ícone -->
      <template v-if="selectedNode.data.shape !== 'frame'">
        <span class="ml-2 text-xs text-n-slate-10">
          {{ t('WORKSPACE_MAPS.ICON') }}
        </span>
        <div
          class="flex flex-wrap items-center gap-0.5 rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            class="px-1.5 py-1 rounded-lg text-[11px] transition-colors"
            :class="
              !selectedNode.data.icon
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="setNodeIcon(selectedNode.id, null)"
          >
            {{ t('WORKSPACE_MAPS.ICON_NONE') }}
          </button>
          <button
            v-for="icon in ICON_KEYS"
            :key="icon"
            class="p-1 rounded-lg transition-colors"
            :class="
              selectedNode.data.icon === icon
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            :title="icon"
            @click="setNodeIcon(selectedNode.id, icon)"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path :d="ICONS[icon]" />
            </svg>
          </button>
        </div>
        <!-- Título + lista -->
        <button
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl border border-n-weak transition-colors"
          :class="
            selectedNode.data.list
              ? 'bg-n-brand text-white border-transparent'
              : 'text-n-slate-11 hover:bg-n-alpha-2 bg-n-solid-1'
          "
          :title="t('WORKSPACE_MAPS.LIST_HINT')"
          @click="toggleList(selectedNode.id)"
        >
          <span class="i-lucide-list w-3.5 h-3.5" />
          {{ t('WORKSPACE_MAPS.LIST') }}
        </button>
      </template>
      <!-- Fonte A− / A+ -->
      <div
        class="ml-2 flex items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        :title="t('WORKSPACE_MAPS.FONT_SIZE')"
      >
        <button
          class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="bumpFontSize(selectedNode.id, -2)"
        >
          <span class="i-lucide-a-arrow-down w-4 h-4" />
        </button>
        <span class="text-xs text-n-slate-11 tabular-nums w-5 text-center">
          {{ selectedNode.data.fontSize || 14 }}
        </span>
        <button
          class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="bumpFontSize(selectedNode.id, 2)"
        >
          <span class="i-lucide-a-arrow-up w-4 h-4" />
        </button>
      </div>
      <!-- Fixar -->
      <button
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl border border-n-weak transition-colors"
        :class="
          selectedNode.data.locked
            ? 'bg-n-brand text-white border-transparent'
            : 'text-n-slate-11 hover:bg-n-alpha-2 bg-n-solid-1'
        "
        :title="t('WORKSPACE_MAPS.LOCK')"
        @click="toggleLock(selectedNode.id)"
      >
        <span
          :class="
            selectedNode.data.locked ? 'i-lucide-lock' : 'i-lucide-lock-open'
          "
          class="w-3.5 h-3.5"
        />
        {{
          selectedNode.data.locked
            ? t('WORKSPACE_MAPS.LOCKED')
            : t('WORKSPACE_MAPS.LOCK')
        }}
      </button>
      <div class="flex-1" />
      <button
        class="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
        @click="removeNode(selectedNode.id)"
      >
        <span class="i-lucide-trash-2 w-3.5 h-3.5" />
        {{ t('WORKSPACE_MAPS.DELETE_BRANCH') }}
      </button>
    </div>

    <!-- Barra da seta selecionada: tipo, traço, espessura, ponta, cor, rótulo -->
    <div
      v-if="selectedEdge && edgeOpts"
      class="mapa-sem-impressao flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 border-b border-n-weak bg-n-solid-2 z-10"
    >
      <!-- Tipo de linha -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_TYPE')
        }}</span>
        <div
          class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            v-for="opcao in EDGE_TYPE_OPTIONS"
            :key="opcao.key"
            class="px-1.5 py-1 rounded-lg transition-colors"
            :class="
              (edgeOpts.lineType || lineShape) === opcao.key
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            :title="t(`WORKSPACE_MAPS.LINE_${opcao.key.toUpperCase()}`)"
            @click="patchEdge(selectedEdge.id, { lineType: opcao.key })"
          >
            <svg
              viewBox="0 0 30 16"
              width="30"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path :d="opcao.d" />
            </svg>
          </button>
        </div>
      </div>
      <!-- Traço -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_PATTERN')
        }}</span>
        <div
          class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            v-for="padrao in PATTERN_OPTIONS"
            :key="padrao"
            class="px-1.5 py-1 rounded-lg transition-colors"
            :class="
              edgeOpts.pattern === padrao
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            :title="t(`WORKSPACE_MAPS.PATTERN_${padrao.toUpperCase()}`)"
            @click="
              patchEdge(selectedEdge.id, { pattern: padrao, dashed: undefined })
            "
          >
            <svg
              viewBox="0 0 30 16"
              width="30"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              :stroke-linecap="padrao === 'dotted' ? 'round' : 'butt'"
            >
              <path
                d="M3 8 H27"
                :stroke-dasharray="
                  padrao === 'dashed'
                    ? '6 4'
                    : padrao === 'dotted'
                      ? '0.5 4.5'
                      : undefined
                "
              />
            </svg>
          </button>
        </div>
      </div>
      <!-- Espessura -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_WIDTH')
        }}</span>
        <div
          class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            v-for="espessura in WIDTH_OPTIONS"
            :key="espessura"
            class="px-1.5 py-1 rounded-lg transition-colors"
            :class="
              edgeOpts.width === espessura
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            :title="t(`WORKSPACE_MAPS.WIDTH_${espessura.toUpperCase()}`)"
            @click="patchEdge(selectedEdge.id, { width: espessura })"
          >
            <svg
              viewBox="0 0 30 16"
              width="30"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              :stroke-width="LINE_WIDTHS[espessura] * 1.2"
            >
              <path d="M4 8 H26" />
            </svg>
          </button>
        </div>
      </div>
      <!-- Ponta -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_ARROW')
        }}</span>
        <div
          class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            v-for="ponta in ARROW_OPTIONS"
            :key="ponta"
            class="px-1.5 py-1 rounded-lg transition-colors"
            :class="
              edgeOpts.arrow === ponta
                ? 'bg-n-brand text-white'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            :title="t(`WORKSPACE_MAPS.ARROW_${ponta.toUpperCase()}`)"
            @click="patchEdge(selectedEdge.id, { arrow: ponta })"
          >
            <svg
              viewBox="0 0 30 16"
              width="30"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 8 H25" />
              <path v-if="ponta !== 'none'" d="M20 4 L26 8 L20 12" />
              <path v-if="ponta === 'both'" d="M10 4 L4 8 L10 12" />
            </svg>
          </button>
        </div>
      </div>
      <!-- Cor -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_COLOR')
        }}</span>
        <button
          class="px-1.5 py-0.5 text-[11px] rounded-lg border transition-colors"
          :class="
            !edgeOpts.color
              ? 'border-n-brand text-n-brand'
              : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t('WORKSPACE_MAPS.EDGE_COLOR_AUTO_HINT')"
          @click="patchEdge(selectedEdge.id, { color: undefined })"
        >
          {{ t('WORKSPACE_MAPS.EDGE_COLOR_AUTO') }}
        </button>
        <button
          v-for="color in COLORS"
          :key="`seta-${color}`"
          class="w-4 h-4 rounded-full border-2 transition-transform hover:scale-110"
          :class="
            edgeOpts.color === color
              ? 'border-n-slate-12'
              : 'border-transparent'
          "
          :style="{ backgroundColor: color }"
          @click="patchEdge(selectedEdge.id, { color })"
        />
      </div>
      <!-- Rótulo -->
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-n-slate-10">{{
          t('WORKSPACE_MAPS.EDGE_LABEL')
        }}</span>
        <input
          id="mapa-rotulo-seta"
          ref="edgeLabelInput"
          :value="selectedEdge.data?.label || ''"
          class="w-40 px-2 py-1 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('WORKSPACE_MAPS.EDGE_LABEL_PLACEHOLDER')"
          @input="patchEdge(selectedEdge.id, { label: $event.target.value })"
          @keydown.enter="$event.target.blur()"
        />
      </div>
      <div class="flex-1" />
      <button
        class="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
        @click="removeEdge(selectedEdge.id)"
      >
        <span class="i-lucide-trash-2 w-3.5 h-3.5" />
        {{ t('WORKSPACE_MAPS.DELETE_EDGE') }}
      </button>
    </div>

    <div v-if="isLoading" class="flex flex-1 items-center justify-center">
      <span
        class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
      />
    </div>

    <div
      v-else
      ref="flowWrapper"
      class="mapa-impressao flex flex-1 min-h-0"
      :class="{ 'mapa-tema-neon': isNeon, 'mapa-exportando': isExporting }"
      :style="{
        '--mapa-fundo': isNeon ? NEON.fundo : 'rgb(var(--background-color))',
      }"
    >
      <VueFlow
        v-model:nodes="nodes"
        v-model:edges="edges"
        class="flex-1"
        :min-zoom="0.2"
        :max-zoom="2.5"
        :delete-key-code="null"
        snap-to-grid
        :snap-grid="[12, 12]"
        connection-mode="loose"
        :elevate-nodes-on-select="false"
        :connect-on-click="false"
        :connection-radius="40"
        fit-view-on-init
        @pane-click="
          selectedId = null;
          editingId = null;
          selectedEdgeId = null;
        "
      >
        <Background
          :gap="24"
          :bg-color="isNeon ? NEON.fundo : undefined"
          :pattern-color="isNeon ? NEON.pontos : undefined"
        />
        <Controls position="bottom-left" class="mapa-sem-impressao" />
        <MiniMap
          pannable
          zoomable
          position="bottom-right"
          class="mapa-sem-impressao"
        />

        <div
          v-if="!nodes.length"
          class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-n-slate-10 pointer-events-none"
        >
          <span class="i-lucide-git-fork w-10 h-10" />
          <p class="text-sm">{{ t('WORKSPACE_MAPS.CANVAS_EMPTY') }}</p>
        </div>

        <!-- MOLDURA: agrupa blocos; rótulo no topo; canto redimensiona -->
        <template #node-frame="{ id, data }">
          <div
            class="mapa-moldura relative rounded-2xl border-2"
            :class="{ 'mapa-neon-moldura': isNeon }"
            :style="{
              width: `${data.width}px`,
              height: `${data.height}px`,
              borderColor: data.color,
              backgroundColor: isNeon ? 'transparent' : `${data.color}0d`,
              '--c': data.color,
            }"
            :data-selecionado="selectedId === id ? 'sim' : null"
            @click.stop="
              selectedId = id;
              selectedEdgeId = null;
            "
            @dblclick.stop="startEdit(id)"
          >
            <div
              class="mapa-moldura-rotulo absolute -top-3.5 left-4 px-3 py-0.5 rounded-lg border-2 text-sm font-bold uppercase tracking-wide"
              :style="{ borderColor: data.color, color: data.color }"
            >
              <input
                v-if="editingId === id"
                :id="`mapa-input-${id}`"
                :value="data.label"
                class="mapa-editor nodrag w-40 font-bold uppercase"
                :placeholder="t('WORKSPACE_MAPS.FRAME_PLACEHOLDER')"
                @input="data.label = $event.target.value"
                @blur="editingId = null"
                @click.stop
              />
              <span v-else>
                {{ data.label || t('WORKSPACE_MAPS.FRAME_PLACEHOLDER') }}
              </span>
            </div>
            <span
              class="mapa-canto nodrag mapa-sem-impressao absolute -right-1.5 -bottom-1.5 w-4 h-4 rounded-sm cursor-nwse-resize"
              :style="{ backgroundColor: data.color }"
              :title="t('WORKSPACE_MAPS.RESIZE')"
              @pointerdown.stop.prevent="startResize($event, id)"
            />
          </div>
        </template>

        <template #node-idea="{ id, data }">
          <div
            class="group/no relative"
            @click.stop="
              selectedId = id;
              selectedEdgeId = null;
            "
            @dblclick.stop="startEdit(id)"
          >
            <!-- LOSANGO: decisão -->
            <div
              v-if="data.shape === 'diamond'"
              class="relative flex items-center justify-center cursor-pointer"
              :style="{
                width: `${data.width}px`,
                height: `${data.height}px`,
                '--c': data.color,
              }"
              :data-selecionado-losango="selectedId === id ? 'sim' : null"
            >
              <svg
                class="absolute inset-0 overflow-visible"
                :width="data.width"
                :height="data.height"
                :viewBox="`0 0 ${data.width} ${data.height}`"
              >
                <polygon
                  :points="`${data.width / 2},2 ${data.width - 2},${data.height / 2} ${data.width / 2},${data.height - 2} 2,${data.height / 2}`"
                  :fill="isNeon ? 'rgba(10,13,20,0.9)' : `${data.color}1f`"
                  :stroke="data.color"
                  stroke-width="2.5"
                  :class="{ 'mapa-neon-losango': isNeon }"
                />
              </svg>
              <textarea
                v-if="editingId === id"
                :id="`mapa-input-${id}`"
                :value="data.label"
                rows="2"
                class="mapa-editor nodrag relative w-3/5 text-center resize-none font-semibold"
                :class="{ 'mapa-letra-mao': isNeon }"
                :style="{ fontSize: `${data.fontSize || 14}px` }"
                :placeholder="t('WORKSPACE_MAPS.NOTE_PLACEHOLDER')"
                @input="data.label = $event.target.value"
                @blur="editingId = null"
                @click.stop
              />
              <p
                v-else
                class="relative w-3/5 text-center font-semibold whitespace-pre-wrap break-words leading-tight"
                :class="
                  isNeon
                    ? 'mapa-letra-mao uppercase text-[#eceff6]'
                    : 'text-n-slate-12'
                "
                :style="{ fontSize: `${data.fontSize || 14}px` }"
              >
                {{ data.label || t('WORKSPACE_MAPS.NOTE_PLACEHOLDER') }}
              </p>
            </div>

            <!-- Demais formatos -->
            <div
              v-else
              class="min-w-[130px] max-w-[300px] flex items-center justify-center px-4 py-2.5 cursor-pointer text-sm font-medium transition-shadow"
              :class="nodeBoxClass(data)"
              :style="nodeBoxStyle(data)"
              :data-selecionado="selectedId === id ? 'sim' : null"
            >
              <span
                v-if="data.locked"
                class="i-lucide-lock absolute -top-1.5 -left-1.5 w-3.5 h-3.5 text-n-slate-9"
              />
              <textarea
                v-if="editingId === id"
                :id="`mapa-input-${id}`"
                :value="data.label"
                :rows="data.list ? 4 : 1"
                class="mapa-editor nodrag w-full resize-none font-medium"
                :class="[
                  data.list ? 'text-left' : 'text-center',
                  { 'mapa-letra-mao': isNeon },
                ]"
                :style="{ fontSize: `${data.fontSize || 14}px` }"
                :placeholder="
                  data.list
                    ? t('WORKSPACE_MAPS.LIST_PLACEHOLDER')
                    : t('WORKSPACE_MAPS.NOTE_PLACEHOLDER')
                "
                @input="data.label = $event.target.value"
                @blur="editingId = null"
                @click.stop
              />
              <div
                v-else
                class="w-full"
                :class="data.list ? 'text-left' : 'text-center'"
                :style="{ fontSize: `${data.fontSize || 14}px` }"
              >
                <div
                  class="flex items-center gap-2"
                  :class="data.list ? '' : 'justify-center'"
                >
                  <svg
                    v-if="data.icon && ICONS[data.icon]"
                    class="shrink-0"
                    :class="{ 'mapa-neon-icone': isNeon }"
                    viewBox="0 0 24 24"
                    :width="Math.round((data.fontSize || 14) * 1.7)"
                    :height="Math.round((data.fontSize || 14) * 1.7)"
                    fill="none"
                    :stroke="
                      isNeon || data.shape !== 'card' ? data.color : '#ffffff'
                    "
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path :d="ICONS[data.icon]" />
                  </svg>
                  <p
                    class="whitespace-pre-wrap break-words"
                    :class="[
                      data.shape === 'note' && !isNeon ? 'text-n-slate-12' : '',
                      isNeon ? 'mapa-neon-titulo mapa-letra-mao' : '',
                      data.list ? 'font-bold' : '',
                    ]"
                  >
                    {{
                      (data.list ? splitList(data.label).title : data.label) ||
                      t('WORKSPACE_MAPS.NOTE_PLACEHOLDER')
                    }}
                  </p>
                </div>
                <ul
                  v-if="data.list && splitList(data.label).items.length"
                  class="mt-1.5 pl-5 list-disc space-y-0.5 font-normal"
                  :class="{ 'mapa-letra-mao mapa-neon-lista': isNeon }"
                >
                  <li
                    v-for="(item, index) in splitList(data.label).items"
                    :key="index"
                  >
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Canto do losango: redimensionar -->
            <span
              v-if="data.shape === 'diamond' && selectedId === id"
              class="mapa-canto nodrag mapa-sem-impressao absolute -right-1 -bottom-1 w-3.5 h-3.5 rounded-sm cursor-nwse-resize"
              :style="{ backgroundColor: data.color }"
              :title="t('WORKSPACE_MAPS.RESIZE')"
              @pointerdown.stop.prevent="startResize($event, id)"
            />

            <!-- "+" dos 4 lados = ALÇA: clicar cria o próximo bloco,
                 arrastar puxa a seta até qualquer parte de outro bloco. -->
            <Handle
              v-for="side in SIDES"
              :id="side"
              :key="side"
              type="source"
              :position="side"
              class="mapa-alca-mais mapa-sem-impressao"
              :class="{
                'mapa-alca-oculta': editingId === id,
                'mapa-alca-visivel': selectedId === id,
              }"
              :style="{ '--alca': data.color }"
              :title="t('WORKSPACE_MAPS.CONNECT_HINT')"
            >
              <!-- "+" desenhado à mão com TAMANHO EM ATRIBUTOS: a fonte de
                   ícones não renderiza no template de nó. -->
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="#ffffff"
                stroke-width="4"
                stroke-linecap="round"
                class="pointer-events-none"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </Handle>
          </div>
        </template>
      </VueFlow>
    </div>
  </div>
</template>

<style scoped>
/* Alça = botão "+" (o tema default do VueFlow pinta bolinhas azuis de 6px —
   daí os !important). Aparece no hover e fica fixa no bloco selecionado. */
.mapa-alca-mais {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  min-height: 24px !important;
  border-radius: 9999px !important;
  border: 2px solid #ffffff !important;
  background: var(--alca) !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  cursor: crosshair !important;
  opacity: 0;
  transition:
    opacity 0.15s,
    scale 0.15s;
  z-index: 20;
}
.mapa-alca-mais.vue-flow__handle-right {
  right: -14px !important;
}
.mapa-alca-mais.vue-flow__handle-left {
  left: -14px !important;
}
.mapa-alca-mais.vue-flow__handle-top {
  top: -14px !important;
}
.mapa-alca-mais.vue-flow__handle-bottom {
  bottom: -14px !important;
}
.group\/no:hover .mapa-alca-mais,
.mapa-alca-visivel {
  opacity: 1;
}
.mapa-alca-mais:hover {
  scale: 1.15;
}
.mapa-alca-oculta {
  opacity: 0 !important;
  pointer-events: none;
}
/* Editor herda a cor do nó e NÃO desenha caixa própria (o CSS global de
   textarea pintava um retângulo escuro com borda no meio do nó). */
.mapa-editor {
  background: transparent !important;
  color: inherit !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  border-radius: 0 !important;
  min-height: 1.4rem !important;
}
.mapa-editor::placeholder {
  color: currentColor;
  opacity: 0.5;
}
.mapa-no-texto {
  background: transparent;
}
/* Contorno de seleção. */
[data-selecionado='sim'] {
  box-shadow: 0 0 0 3px rgba(39, 129, 246, 0.4);
}
[data-selecionado-losango='sim'] {
  outline: 2px dashed rgba(39, 129, 246, 0.6);
  outline-offset: 4px;
}

/* ===== Tema NEON: lousa de vidro escura, traço brilhante, letra de marcador ===== */
.mapa-letra-mao {
  font-family: 'Kalam', 'Comic Sans MS', 'Segoe Print', cursive;
}
.mapa-neon-no {
  color: #eceff6;
  background: rgba(10, 13, 20, 0.86);
  border: 2px solid var(--c);
  box-shadow:
    0 0 14px color-mix(in srgb, var(--c) 55%, transparent),
    inset 0 0 10px color-mix(in srgb, var(--c) 18%, transparent);
}
.mapa-neon-texto {
  background: transparent;
  border: none;
  box-shadow: none;
}
.mapa-neon-titulo {
  color: var(--c);
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-shadow: 0 0 8px color-mix(in srgb, var(--c) 70%, transparent);
}
.mapa-neon-lista {
  color: #eceff6;
}
.mapa-neon-lista li::marker {
  color: var(--c);
}
.mapa-neon-icone,
.mapa-neon-losango {
  filter: drop-shadow(0 0 5px var(--c));
}
.mapa-neon-moldura {
  box-shadow:
    0 0 16px color-mix(in srgb, var(--c) 45%, transparent),
    inset 0 0 12px color-mix(in srgb, var(--c) 12%, transparent);
}
.mapa-moldura-rotulo {
  background: var(--mapa-fundo);
}
.mapa-tema-neon .mapa-moldura-rotulo {
  font-family: 'Kalam', 'Comic Sans MS', 'Segoe Print', cursive;
  text-shadow: 0 0 8px color-mix(in srgb, var(--c) 70%, transparent);
}
.mapa-tema-neon :deep(.vue-flow__edge-path) {
  filter: drop-shadow(0 0 4px currentColor);
}
/* PNG: controles, minimapa, alças e botões + ficam fora da imagem. */
.mapa-exportando :deep(.mapa-sem-impressao),
.mapa-exportando :deep(.vue-flow__handle) {
  display: none !important;
}
.mapa-tema-neon :deep(.vue-flow__edge-textwrapper),
.mapa-tema-neon :deep(.vue-flow__edge-text) {
  font-family: 'Kalam', 'Comic Sans MS', 'Segoe Print', cursive;
  text-transform: uppercase;
}
</style>

<style>
/* PDF = impressão do navegador: só o canvas do mapa, paisagem, com cores. */
@media print {
  @page {
    size: landscape;
    margin: 8mm;
  }
  body * {
    visibility: hidden !important;
  }
  .mapa-impressao,
  .mapa-impressao * {
    visibility: visible !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .mapa-impressao {
    position: fixed !important;
    inset: 0 !important;
  }
  .mapa-impressao .mapa-sem-impressao {
    display: none !important;
  }
}
</style>
